/**
 * The inline markup grammar the assistant writes and the web client renders.
 *
 * Lives in the leaf package because the grammar has readers on both sides of
 * the wire and they cannot import each other: the eval scenario greps model
 * output with it to grade the directive that teaches it, the web client
 * splits message text on it to draw the chips, and the SMS and email senders
 * render it to words (`@orn/async`'s `assistant/reply/renderInlineMarkupAsText.ts`,
 * reading the fallback words below). A copy per call site drifts
 * silently — a pattern one side accepts and the other does not is a chip that
 * renders permanently unresolved, with nothing failing to say so, which is
 * exactly the failure a single shared definition rules out.
 *
 * Three grammars:
 *
 * - **Entity reference** — `<merchant_id=UUID />`, `<financial_account_id=UUID />`,
 *   `<transaction_id=UUID />`, `<bill_id=UUID />`,
 *   `<recurring_transaction_id=UUID />`. The type names match the tool
 *   surface's argument names, so the model reads one vocabulary for a record
 *   whether it is asking about one or pointing at it. Names a row the user can
 *   see; the client resolves the label, so the model authors no name that can
 *   disagree with the row.
 * - **Suggestion** — `<suggestion="Contact mom for context" />`. Names an
 *   action, so its text *is* the payload — those words are sent back verbatim
 *   when the user taps it.
 * - **Date macro** — `<date=2026-08-18 />`. The one grammar that is a text
 *   substitution rather than a drawn element: every surface rewrites it against
 *   the reader's clock ("today" while it still is, the date once it isn't).
 *   Pattern and substitution live in {@link ./dateMacro.ts}.
 *
 * **The `=` is load-bearing.** micromark refuses to read either as HTML (a tag
 * name cannot be followed by `=`), so both survive markdown parsing inside an
 * ordinary text node instead of being classified as raw HTML and dropped. The
 * underscore in the entity form matters for the same reason — a hyphen is a
 * legal tag-name character.
 */

import { isValidUuid } from '../ids/uuid.js';

import { isRenderableDateMacro } from './dateMacro.js';

/** The record types an entity reference can name. */
export const ENTITY_REF_TYPES = [
  'merchant',
  'financial_account',
  'transaction',
  'bill',
  'recurring_transaction',
] as const;
export type EntityRefType = (typeof ENTITY_REF_TYPES)[number];

/**
 * What an entity reference renders as when its id resolves to nothing —
 * hallucinated by the model, scoped away from this reader, or unreadable at
 * send time. One set of words for every surface: the web chip
 * (`@orn/shared-web`'s `lib/assistant/entityChipLabel.ts`) and the plain-text
 * renderer the SMS and email senders run (`@orn/async`'s
 * `assistant/reply/renderInlineMarkupAsText.ts`) both read it, so a miss
 * degrades the same way whether or not a chip is drawn.
 *
 * A fallback is mandatory in a way it would not be for a linked name: the model
 * authors no label, so there is no inline text to degrade to. Rendering nothing
 * would leave a hole in the sentence ("a charge at  today"), and rendering the
 * token would put raw markup in front of the user.
 *
 * Each entry has to slot into the grammar the model wrote for the *resolved*
 * label, which is why the transaction fallback carries no determiner — a reply
 * shaped for "a larger-than-usual 75.15" reads as "a larger-than-usual this
 * transaction" the moment the lookup misses.
 */
export const UNRESOLVED_ENTITY_REF_LABEL: Readonly<Record<EntityRefType, string>> = {
  merchant: 'this merchant',
  financial_account: 'this account',
  transaction: 'transaction',
  bill: 'this bill',
  recurring_transaction: 'this recurring payment',
};

/**
 * Capture groups: 1 = record type, 2 = id.
 *
 * The id is matched loosely (any run without whitespace or a closing bracket)
 * rather than as a strict UUID, so a malformed id still consumes its token and
 * degrades to a chip that says what it is. A stricter pattern would leave the
 * raw `<merchant_id=… />` markup on screen — the one outcome worth ruling out.
 *
 * **The closing ` />` is required**, and there is one grammar rather than a
 * strict writer and a lenient reader. A token missing it is not drawn, so a
 * stored summary carrying one would replay as raw markup forever — which is
 * why {@link unrenderableMarkupIn} exists and the work-item write path refuses
 * such a call outright, rather than the renderer widening to absorb it.
 *
 * A factory rather than a shared `const`: a `/g/` regex carries mutable
 * `lastIndex`, so one instance reused across `matchAll` and `.test()` call
 * sites in different packages would return different answers depending on who
 * ran last.
 */
export const entityRefPattern = (): RegExp =>
  /<(merchant|financial_account|transaction|bill|recurring_transaction)_id=([^\s/>]+)\s*\/>/g;

/**
 * Capture group 1 = the suggestion's text. See {@link entityRefPattern} on why
 * this is a factory.
 *
 * **The text may contain double quotes** — `Ask "is this familiar?"` is an
 * ordinary thing to suggest, and a grammar that ended the capture at the first
 * inner quote would leave the whole token on screen as raw markup. So the
 * capture runs to the closing `" />` rather than to the next `"`.
 *
 * `>` is what bounds it. Excluding that one character keeps the capture from
 * running past its own token into a later one, so an unterminated suggestion
 * spoils only itself instead of swallowing the well-formed tokens after it.
 *
 * The SMS and email renderer alone reads a lenient superset of this — no slash,
 * curly quotes — because nothing downstream of it can draw a near-miss; the web
 * client and the write guard stay on this one.
 */
export const suggestionPattern = (): RegExp => /<suggestion="([^>]*?)"\s*\/>/g;

/**
 * A fenced block, one pattern per delimiter, because the two do not close each
 * other: a fence opened with backticks and "closed" with tildes is still open,
 * and a parser reads the rest of the text as code. One pattern with a shared
 * group cannot express that — it would accept either delimiter as the closer.
 *
 * The closer is the opener's run or longer, which CommonMark allows. What
 * neither branch closes falls to the unclosed branch and strips to the end.
 */
const BACKTICK_FENCE = /(?:^|\n) {0,3}(`{3,})[^\n]*(?:\n[\s\S]*?(?:\n {0,3}\1`*[ \t]*(?=\n|$))|[\s\S]*$)/g;
const TILDE_FENCE = /(?:^|\n) {0,3}(~{3,})[^\n]*(?:\n[\s\S]*?(?:\n {0,3}\1~*[ \t]*(?=\n|$))|[\s\S]*$)/g;

/**
 * An indented code block: four spaces or a tab at line start. CommonMark reads
 * the line as code, so remark gives it a `code` node and the splitter never
 * descends into it.
 */
const INDENTED_BLOCK = /(?:^|\n)(?: {4}|\t)[^\n]*/g;

/**
 * A code span: a run of backticks closed by a run of the same length, so
 * ` ``a `b` c`` ` is one span rather than two.
 *
 * Spans may cross lines but not a blank one — that ends the paragraph, and
 * without the bound a single stray backtick would swallow the rest of the
 * reply and hide every real suggestion after it.
 */
const CODE_SPAN = /(`+)(?:(?!\n[ \t]*\n)[\s\S])*?\1/g;

/**
 * Blank out a code region in place, one `>` per character, newlines kept.
 *
 * **Masking rather than deleting, and the difference is a bug.** Deleting joins
 * text the parser keeps apart: in `<suggestion="Delete `this` record" />` the
 * span splits the paragraph into three nodes, so the client draws no chip —
 * but delete the span and the halves close up into a token that matches. `>`
 * is the filler because neither grammar's capture may contain one, so any token
 * reaching across a masked region fails instead. Newlines survive so the passes
 * after this one still see the line structure they match on.
 */
const mask = (region: string): string => region.replace(/[^\n]/g, '>');

/**
 * Blank what markdown renders as code. Fenced blocks first: a fence's body may
 * contain backticks of its own, so masking spans first would leave the fence's
 * delimiters behind and re-expose its contents.
 *
 * A regex approximation of the client's parser rather than a shared AST walk —
 * `@orn/global` is a leaf package and may not take a markdown runtime.
 *
 * Known limit: container blocks. A fence inside a block quote or a list item
 * carries its own `>` or indent prefix, which none of these patterns model, so
 * a suggestion nested that deep is judged on the plain-text rules alone. The
 * grammar asks for suggestions on their own lines at the end of a reply, so a
 * chip nested inside a quoted list is not a shape this has to get right.
 */
const maskCode = (text: string): string =>
  text.replace(BACKTICK_FENCE, mask).replace(TILDE_FENCE, mask).replace(INDENTED_BLOCK, mask).replace(CODE_SPAN, mask);

/**
 * A label markdown has no way to break apart.
 *
 * **This is a sufficient condition, not an imitation of the parser**, and the
 * difference is why it can be complete. Asking "would markdown split this?"
 * means reimplementing CommonMark — emphasis, links, entities, hard breaks, and
 * whatever the spec adds next. Asking "is this provably unsplittable?" is a
 * character check: a label with no construct-opening character and no newline
 * sits whole inside one `text` node, which is the only thing the splitter walks,
 * so the client draws it.
 *
 * A label failing this is a defect on its own terms, whatever the parser does
 * with it. `SuggestionChip` renders the label as button text rather than as
 * markdown, so `**Dismiss it?**` reaches the user with its asterisks showing —
 * on a chip if one is drawn at all.
 *
 * `&` is deliberately absent: CommonMark leaves a bare ampersand literal, and
 * merchant names carry them ("PG&E", "AT&T"). Only a run shaped like a
 * character reference does anything, and {@link CHARACTER_REFERENCE_SHAPE}
 * refuses those separately.
 */
const PLAIN_LABEL = /^[^*_`[\]<>\\~\n]*$/;

/**
 * A run shaped like a CommonMark character reference — `&name;`, `&#digits;`,
 * `&#xhex;` (bounds per spec: HTML5 entity names are ASCII alphanumeric and at
 * most 32 characters; numeric refs allow up to 7 decimal or 6 hex digits).
 *
 * A label carrying one is refused for two reasons. micromark decodes the
 * reference before the splitter runs, so `&gt;` becomes `>` inside the token
 * and the capture (`[^>]*?`) stops matching — raw markup on screen. And a
 * reference that decodes cleanly (`&amp;`) draws a chip whose text differs
 * from the stored label, so the server's answer about what was offered drifts
 * from what the client draws and sends back on tap.
 *
 * **Shape, not the HTML5 entity list** — the list cannot ship in a leaf
 * package, so this over-approximates what CommonMark decodes: `Q&A;` is not a
 * real entity and would stay literal, but it is refused anyway. A rare false
 * refusal is the safe side; the reverse draws an undrawable token.
 */
const CHARACTER_REFERENCE_SHAPE = /&(?:[a-zA-Z][a-zA-Z0-9]{0,31}|#[0-9]{1,7}|#[xX][0-9a-fA-F]{1,6});/;

/** Both label conditions; see {@link PLAIN_LABEL} and {@link CHARACTER_REFERENCE_SHAPE}. */
const isPlainLabel = (label: string): boolean => PLAIN_LABEL.test(label) && !CHARACTER_REFERENCE_SHAPE.test(label);

interface IDrawableSuggestion {
  label: string;
  start: number;
  end: number;
}

/**
 * Every suggestion the client would turn into a chip, with where it sits in the
 * original text. Two conditions, and the token needs both: it must not be inside
 * a code region ({@link maskCode} — that is about its surroundings), and its
 * label must be plain ({@link isPlainLabel} — that is about the token itself).
 *
 * The one source of truth for what counts as offered, so the exports below
 * cannot come to different answers about the same reply.
 */
const drawableSuggestions = (text: string): IDrawableSuggestion[] =>
  [...maskCode(text).matchAll(suggestionPattern())]
    .filter((m) => isPlainLabel(m[1]))
    .map((m) => ({ label: m[1], start: m.index, end: m.index + m[0].length }));

/**
 * Every entity reference the client would actually draw as a chip, as the type
 * and id it names.
 *
 * Code-aware for the same reason {@link renderableSuggestionsIn} is: the
 * renderer splits `text` nodes only, and a code span or fence carries its
 * content on `value`, so a token quoted inside one is never touched and reaches
 * the reader as literal markup. Counting it would let "did it reference the
 * row?" pass on something nobody can see a chip for.
 */
export const renderableEntityRefsIn = (text: string): { refType: string; refId: string }[] =>
  [...maskCode(text).matchAll(entityRefPattern())].map((m) => ({ refType: m[1], refId: m[2] }));

/** The suggestion labels the client would actually draw as chips. */
export const renderableSuggestionsIn = (text: string): string[] => drawableSuggestions(text).map((s) => s.label);

/**
 * Labels that matched the grammar but that the client will not draw — fenced,
 * or carrying markdown that splits the token across nodes. Each one reaches the
 * user as raw markup, so nothing else would notice it went out.
 */
export const undrawableSuggestionsIn = (text: string): string[] => {
  // Matched against the ORIGINAL text, not the masked copy: a fenced token is
  // masked out of existence, so searching the copy would report the one failure
  // this exists to name as no token at all. Offsets survive masking, so a
  // drawable match's position identifies it in either string.
  const drawn = new Set(drawableSuggestions(text).map((s) => s.start));
  return [...text.matchAll(suggestionPattern())].filter((m) => !drawn.has(m.index)).map((m) => m[1]);
};

/**
 * The reply with its tappable suggestions taken out, and everything else — a
 * token the client cannot draw among it — left exactly as the user reads it.
 *
 * The split is between what becomes a chip and what reaches the user as text,
 * which is the line every caller needs: an undrawable token renders as raw
 * markup, so it is words on screen and stays. Why a given caller wants the
 * chips gone belongs at that call site, not here.
 */
export const withoutRenderableSuggestions = (text: string): string => {
  let out = '';
  let cursor = 0;
  for (const s of drawableSuggestions(text)) {
    out += text.slice(cursor, s.start);
    cursor = s.end;
  }
  return out + text.slice(cursor);
};

/**
 * How many ids of one record type a single resolution query may carry.
 *
 * Matches the ceiling `getTransactions` rejects past; the merchant and account
 * batch reads accept an unbounded list, so for those this is the client
 * declining to send one rather than a bound being enforced on it. A transcript
 * past this is pathological either way — the tokens are model-authored — so the
 * tail is dropped rather than paged.
 */
export const MAX_ENTITY_REF_IDS = 500;

/**
 * Anything shaped like one of our tags but matching neither grammar — an
 * invented type (`<category_id=… />`) the model reached for by analogy, a
 * miscased one (`<Transaction_id=… />`), or one left unclosed
 * (`<suggestion="…">` with no `/>`).
 *
 * Such a tag reaches the user as literal markup: the client's splitter only
 * knows the two grammars above, so an unrecognized one skips the
 * degrade-to-a-chip path entirely. The self-closing slash is optional and the
 * letters case-blind for that same reason — near-misses of the real grammars
 * are exactly what this exists to catch. Exported so the eval can fail on it,
 * since nothing else would notice.
 */
export const unknownMarkupTagPattern = (): RegExp => /<[a-zA-Z][a-zA-Z_]*(?:_id)?=[^>]*\/?>/g;

/**
 * Every token shaped like one of ours that the client will not draw, in the
 * order it was written — an invented type, a miscased one, a missing ` />`.
 *
 * The one definition of "malformed", shared by the work-item write guard that
 * refuses such a call and the eval that grades the same offense. Two copies
 * would let a write be accepted and then graded as a defect, or the reverse.
 *
 * Only names tokens of no grammar. A suggestion that matches the grammar
 * but still cannot be drawn — fenced, or carrying a label markdown splits — is
 * {@link undrawableSuggestionsIn}'s, and a caller wanting both asks both. A
 * date macro counts only when its payload is a real calendar date: a
 * digit-shaped impossible one (`<date=2026-02-30 />`) is named here, because
 * no surface would substitute it and it would reach the user as raw markup.
 */
export const unrenderableMarkupIn = (text: string): string[] =>
  [...text.matchAll(unknownMarkupTagPattern())]
    .map((m) => m[0])
    .filter((tag) => !entityRefPattern().test(tag) && !suggestionPattern().test(tag) && !isRenderableDateMacro(tag));

/**
 * Every entity reference whose id cannot address a row — a placeholder, a name,
 * a shortened id — in the order it was written.
 *
 * {@link entityRefPattern} matches such a token so it still degrades to a chip,
 * but both resolvers drop any id that is not a whole uuid before they query, so
 * it renders as its fallback words for every reader. A shortened id is named
 * too: it can open more than one row. Shared by the work-item write guard and
 * the eval that grades the same offense, for the reason
 * {@link unrenderableMarkupIn} is.
 */
export const unaddressableEntityRefsIn = (text: string): string[] =>
  [...text.matchAll(entityRefPattern())].filter((m) => !isValidUuid(m[2])).map((m) => m[0]);

/**
 * Every markup token in a field that renders as plain text.
 *
 * `user_title` and `user_body` reach the household through list rows and
 * notifications, which render no markup at all — so a *well-formed* token is
 * as wrong there as a malformed one, and this deliberately does not filter the
 * real grammars out the way {@link unrenderableMarkupIn} does.
 */
export const markupTokensIn = (text: string): string[] =>
  [...text.matchAll(unknownMarkupTagPattern())].map((m) => m[0]);

/**
 * Every escape sequence typed out as text — a backslash followed by `n`, `r`,
 * `t`, or `"` — in the order it was written.
 *
 * No card field has a use for one: a tool argument is decoded before it gets
 * here, so a sequence still present is two literal characters the household
 * would read. Shared by the work-item write guard and the eval that grades the
 * same offense, for the reason {@link unrenderableMarkupIn} is.
 *
 * **Deliberately not code-aware.** Text escaped this way has no real line
 * breaks, so it is one physical line — and {@link CODE_SPAN} stops only at a
 * blank one. Masking would let any two stray backticks hide every sequence
 * between them.
 */
export const literalEscapeSequencesIn = (text: string): string[] => [...text.matchAll(/\\[nrt"]/g)].map((m) => m[0]);
